import type { HttpEvent, HttpResponse, HttpUploadProgressEvent } from '@angular/common/http';
import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpEventType,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable, UnaryFunction } from 'rxjs';
import { NEVER, pipe } from 'rxjs';
import { last, map, share, tap } from 'rxjs/operators';

import type { IAppConfig } from '@core/common-legacy';
import {
  APP_CONFIG,
  AUTH_METHOD,
  AUTH_TOKEN,
  COMPANY_ID,
  Helpers,
  PATH_WHITELIST,
} from '@core/common-legacy';
import { HttpApi, HttpApiRequestOptions, HttpRequestData } from '@core/http-types';

import { HttpActions } from './http-actions';

@Injectable()
export abstract class HttpApiErrorHandlerService {
  public abstract handleError(url: string, preventDefault?: boolean): UnaryFunction<any, any>;
}

export const HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN = new HttpContextToken(() => false);

const injectStoreOptional = () => {
  try {
    return inject(Store, { optional: true });
  } catch {
    return null;
  }
};

@Injectable({ providedIn: 'root' })
export class HttpApiService implements HttpApi {
  public static readonly defaultOptions: Required<HttpApiRequestOptions> = {
    skipURLBuild: false,
    skipCompany: false,
    addToken: true,
    responseType: 'json',
    headers: {},
    httpContext: new HttpContext(),
  };
  public static defaultHeaders = {
    'x-json-accent': 'pascal',
  };

  private readonly http = inject(HttpClient);
  private readonly errorHandlerService = inject(HttpApiErrorHandlerService);
  private readonly pathWhitelist: string[] | null = inject(PATH_WHITELIST, { optional: true });
  public readonly authToken = inject(AUTH_TOKEN);
  public readonly currentCompanyId = inject(COMPANY_ID) || null;
  public readonly authMethod = inject(AUTH_METHOD);
  public readonly appConfig = <IAppConfig>inject(APP_CONFIG) ?? {};

  public configApiUrl: string;

  private store = injectStoreOptional();

  constructor(protected httpRequestData: HttpRequestData) {}

  public getDefaultHeaders(): Record<string, string> {
    return {
      ...HttpApiService.defaultHeaders,
      ...this.httpRequestData.provideAuthHeaders(),
      ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
    };
  }

  public createSearchParams(params: Record<string, any>): HttpParams {
    let search = new HttpParams({
      encoder: {
        encodeKey: k => k,
        encodeValue: v => encodeURIComponent(v),
        decodeKey(key: string): string {
          return key;
        },
        decodeValue(value: string): string {
          return value;
        },
      },
    });

    Object.keys(params || {}).forEach(key => {
      const value = params[key];
      if (value != null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => (search = search.append(`${key}[]`, v)));
        } else {
          search = search.set(key, value);
        }
      }
    });

    return search;
  }

  public get<R = any>(
    path: string,
    params?: any,
    options?: HttpApiRequestOptions | null,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = requestOptions.skipURLBuild
      ? path
      : this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .get(url, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
        context: options?.httpContext,
      })
      .pipe(
        this.parseResponse(requestOptions),
        this.errorHandlerService.handleError(
          url,
          options?.httpContext?.get(HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN),
        ),
        share(),
      );
  }

  public post<R = any>(
    path: string,
    params?: any,
    body?: object | null,
    options?: HttpApiRequestOptions | null,
    progressEmitterUuid: string | null = null,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }

    const responseType = options?.responseType === undefined ? 'json' : options?.responseType;

    const requestOptions = this.assignDefaultOptions(options);
    const url = requestOptions.skipURLBuild
      ? path
      : this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);
    const emitProgressEvents = !!progressEmitterUuid;

    return this.http
      .post(url, body, {
        observe: <'response'>(emitProgressEvents ? 'events' : 'response'),
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
          ...(options ? options.headers : {}),
        },
        responseType: <any>responseType,
        reportProgress: emitProgressEvents,
        params: searchParams,
        context: options?.httpContext,
      })
      .pipe(
        !!progressEmitterUuid ? this.handleProgress(progressEmitterUuid) : pipe(),
        this.parseResponse(requestOptions),

        this.errorHandlerService.handleError(
          url,
          options?.httpContext?.get(HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN),
        ),
        share(),
      );
  }

  public put<R = any>(
    path: string,
    params?: any,
    body?: object,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .put(url, body, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
        context: options?.httpContext,
      })
      .pipe(
        this.parseResponse(requestOptions),
        this.errorHandlerService.handleError(
          url,
          options?.httpContext?.get(HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN),
        ),
        share(),
      );
  }

  public patch<R = any>(
    path: string,
    params?: any,
    body?: object | null,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .patch(url, body, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
        context: options?.httpContext,
      })
      .pipe(
        this.parseResponse(requestOptions),
        this.errorHandlerService.handleError(
          url,
          options?.httpContext?.get(HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN),
        ),
        share(),
      );
  }

  public delete<R = any>(
    path: string,
    params?: any,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .delete(url, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(this.appConfig.isE2e),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
        context: options?.httpContext,
      })
      .pipe(
        this.parseResponse(requestOptions),
        this.errorHandlerService.handleError(
          url,
          options?.httpContext?.get(HTTP_PREVENT_DEFAULT_ON_ERROR_TOKEN),
        ),
        share(),
      );
  }

  private parseResponse = (options: Required<HttpApiRequestOptions>) =>
    pipe(map((response: HttpResponse<any>) => Helpers.parseResponse(response)));

  private assignDefaultOptions(
    options?: HttpApiRequestOptions | null,
  ): Required<HttpApiRequestOptions> {
    return { ...HttpApiService.defaultOptions, ...(options || {}) };
  }

  private handleProgress = (progressEmitterUuid: string) =>
    pipe(
      tap((event: HttpEvent<any>) => {
        const progressEvent = <HttpUploadProgressEvent>event;
        if (HttpEventType.UploadProgress === progressEvent.type) {
          this.store?.dispatch(
            HttpActions.uploadProgress({
              payload: { uuid: progressEmitterUuid, event: progressEvent },
            }),
          );
        }
      }),
      last(),
    );

  private isWhitelisted(path: string): boolean {
    if (!this.pathWhitelist) return true;

    const isWhtielisted = this.pathWhitelist.includes(path);
    if (!isWhtielisted) {
      console.warn(`Path: "${path}" is not whitelisted`);
    }
    return isWhtielisted;
  }
}
